import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '../../../app/LanguageContext';

function SortableProductTypeRow({
  type,
  onNameChange,
  onNameBlur,
  onDelete,
  disabled = false,
  saving = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: type.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell width={48} sx={{ px: 1 }}>
        <IconButton
          size="small"
          disabled={disabled}
          {...attributes}
          {...listeners}
          sx={{ cursor: disabled ? 'default' : 'grab', '&:active': { cursor: 'grabbing' } }}
          aria-label="Reorder"
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          size="small"
          value={type.name}
          onChange={(e) => onNameChange?.(type.id, e.target.value)}
          onBlur={() => onNameBlur?.(type)}
          disabled={saving}
        />
      </TableCell>
      <TableCell align="right" width={56}>
        <IconButton color="error" size="small" onClick={() => onDelete?.(type)} disabled={saving}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default function ProductTypesSortableList({
  types = [],
  loading = false,
  reordering = false,
  savingId = null,
  onReorder,
  onNameChange,
  onNameBlur,
  onDelete,
  tableContainer = true,
  size = 'medium',
}) {
  const { translate } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const oldIndex = types.findIndex((row) => row.id === active.id);
    const newIndex = types.findIndex((row) => row.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = arrayMove(types, oldIndex, newIndex);
    onReorder?.(next);
  };

  const table = (
    <Table size={size}>
      <TableHead>
        <TableRow>
          <TableCell width={48} />
          <TableCell>{translate('products.name')}</TableCell>
          <TableCell width={56} />
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={3} align="center">
              {translate('common.loading')}
            </TableCell>
          </TableRow>
        ) : types.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} align="center">
              {translate('products.product_type_none')}
            </TableCell>
          </TableRow>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={types.map((row) => row.id)} strategy={verticalListSortingStrategy}>
              {types.map((type) => (
                <SortableProductTypeRow
                  key={type.id}
                  type={type}
                  onNameChange={onNameChange}
                  onNameBlur={onNameBlur}
                  onDelete={onDelete}
                  disabled={reordering}
                  saving={savingId === type.id || reordering}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </TableBody>
    </Table>
  );

  if (!tableContainer) return table;

  return <TableContainer component={Paper}>{table}</TableContainer>;
}
